import { elasticsearchService } from './elasticsearchService';
import type { ErasureOutcome } from './dataControllerApi';
import { compactUuid, dashedUuid } from '../utils/dataControllerFormat';

/**
 * Data Controller — the deletion steps only the browser can run.
 *
 * Elasticsearch sits behind Cloudflare Access with credentials the frontend holds; argus-backend
 * has none. So after the backend has de-identified its own rows, Tesseract has deleted the account
 * and the backend has erased the Redis identity fields over the game tunnel (the browser has no
 * Redis access at all), the browser runs these and reports each outcome back to be stored on the
 * same erasure record:
 *
 *   Player search index the `cdu-players-v1` document(s) — that index exists so staff can look a
 *                      player up by IP address
 *   Shop editor log    the `quasar-shopdata-changelog` documents keep their edit content; the IP,
 *                      session token and user agent are removed from them
 *
 * Every step is independent: a failure is reported as `failed` with the reason and the others
 * still run, so the record shows exactly what is left to do by hand.
 */

export const PLAYER_INDEX = 'cdu-players-v1';
export const SHOP_CHANGELOG_INDEX = 'quasar-shopdata-changelog';
/** Fields stripped from shop-editor changelog documents. */
export const SHOP_CHANGELOG_FIELDS_TO_REMOVE = ['ip', 'sessionJti', 'userAgent'] as const;

export interface BrowserErasureInput {
  uuid: string;
  discordId?: string | null;
  onProgress?: (outcomes: ErasureOutcome[]) => void;
}

const ES = 'Elasticsearch';

const failure = (system: string, step: string, error: unknown): ErasureOutcome => ({ system, step, action: 'failed', detail: error instanceof Error ? error.message : String(error) });

const sameUuid = (a: unknown, uuid: string) => typeof a === 'string' && compactUuid(a) === compactUuid(uuid);

interface PlayerHit {
  _id: string;
  _source?: { uuid?: string };
}

async function erasePlayerIndex(uuid: string): Promise<ErasureOutcome> {
  const step = 'player search index document';
  const forms = [dashedUuid(uuid), compactUuid(uuid)];
  const result = await elasticsearchService.searchIndex<{ hits?: { hits?: PlayerHit[] } }>(PLAYER_INDEX, {
    size: 50,
    _source: ['uuid'],
    query: { bool: { should: [{ terms: { uuid: forms } }, { terms: { 'uuid.keyword': forms } }], minimum_should_match: 1 } },
  });
  const hits = (result.hits?.hits ?? []).filter((h) => sameUuid(h._source?.uuid, uuid));
  let deleted = 0;
  for (const hit of hits) if (await elasticsearchService.deleteDocument(PLAYER_INDEX, hit._id)) deleted += 1;
  return { system: ES, step, action: deleted > 0 ? 'deleted' : 'nothing', count: deleted };
}

async function eraseShopChangelogIdentifiers(uuid: string, discordId: string | null | undefined): Promise<ErasureOutcome> {
  const step = 'shop editor changelog (IP address, session token, user agent)';
  const ids = [dashedUuid(uuid), compactUuid(uuid), ...(discordId ? [discordId] : [])];
  const updated = await elasticsearchService.updateByQuery(SHOP_CHANGELOG_INDEX, {
    query: { bool: { should: [{ terms: { userId: ids } }, { terms: { 'userId.keyword': ids } }], minimum_should_match: 1 } },
    script: { lang: 'painless', source: SHOP_CHANGELOG_FIELDS_TO_REMOVE.map((f) => `ctx._source.remove('${f}')`).join('; ') + ';' },
  });
  return { system: ES, step, action: updated > 0 ? 'de-identified' : 'nothing', count: updated };
}

/** Runs every browser-side step and returns their outcomes (never throws). */
export async function runBrowserErasureSteps(input: BrowserErasureInput): Promise<ErasureOutcome[]> {
  const outcomes: ErasureOutcome[] = [];
  const emit = () => input.onProgress?.([...outcomes]);

  try {
    outcomes.push(await erasePlayerIndex(input.uuid));
  } catch (e) {
    outcomes.push(failure(ES, 'player search index document', e));
  }
  emit();

  try {
    outcomes.push(await eraseShopChangelogIdentifiers(input.uuid, input.discordId));
  } catch (e) {
    outcomes.push(failure(ES, 'shop editor changelog (IP address, session token, user agent)', e));
  }
  emit();

  outcomes.push(
    { system: ES, step: 'chat, command, spawn, economy and compensation logs', action: 'retained', detail: 'Gameplay records keyed by the Minecraft UUID; kept under the privacy policy and the log retention.' },
    { system: 'IdentAware', step: 'relationship analysis', action: 'retained', detail: 'Derived from the same gameplay logs; ages out under its six-month retention.' },
    { system: 'Browser cache', step: 'username cache entry', action: 'nothing', detail: 'Expires on its own within 30 days on each staff device.' }
  );
  emit();
  return outcomes;
}
